import { Box, CircularProgress, Stack } from '@mui/material'
import { IndustryCategoryEnum } from '@ulysses-inc/harami_api_client'
import * as qs from 'qs'
import { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useNotificationService } from 'src/features/notificationService/useNotificationService'
import IndustryCardGrid from 'src/features/templateEdit/common/IndustryCardGrid'
import { useQueryIndustryTemplates } from 'src/features/templateEdit/onboarding/api/useQueryIndustryTemplates'
import { IndustryGroups } from 'src/features/templateEdit/onboarding/ShowIndustryInfo'
import TemplateHeader from 'src/features/templateEdit/onboarding/TemplateHeader'
import type { IndustryInfo } from 'src/features/templateEdit/onboarding/types'

type Settings = {
  title: string
  subTitle?: string
  backTitle: string
  showIndustries: IndustryInfo[][]
  onBack: () => void
  onClick: (industry: IndustryInfo) => void
}

export const CreateTemplateIndustryScene = () => {
  const history = useHistory()
  const location = useLocation()
  const { showNotification } = useNotificationService()

  const searchParams = qs.parse(history.location.search, {
    ignoreQueryPrefix: true,
  })
  const industryParam = searchParams['industry']
  const industryCategory: IndustryCategoryEnum | undefined = industryParam
    ? (Number(industryParam) as IndustryCategoryEnum)
    : undefined
  const keyParam = searchParams['key']
  const key = typeof keyParam === 'string' ? keyParam : undefined

  const isSecondScreen = location.pathname === '/templates/create/type'

  const {
    data: industryTemplates,
    isLoading,
    isError,
  } = useQueryIndustryTemplates({
    industry: industryCategory ?? IndustryCategoryEnum.FOOD,
    key,
    enabled: isSecondScreen,
  })

  useEffect(() => {
    if (isSecondScreen && isError) {
      showNotification('テンプレート一覧の取得に失敗しました', {
        notificationType: 'error',
      })
    }
  }, [isSecondScreen, isError, showNotification])

  const settingsForFirstScreen: Settings = {
    title: 'ひな形をお選びください',
    subTitle:
      '空のひな形もしくは業界ごとのおすすめテンプレートから作成してください。',
    backTitle: '戻る',
    showIndustries: IndustryGroups,
    onBack: () => {
      history.push('/templates')
    },
    onClick: (industry: IndustryInfo) => {
      if (industry.title === '空のひな形') {
        history.push('/templates/pages/add')
      } else {
        const url = `/templates/create/type?industry=${industry.type}`
        history.push(key ? `${url}&key=${key}` : url)
      }
    },
  }

  const templateInfoList: IndustryInfo[][] = industryTemplates
    ? [
        industryTemplates.map(template => ({
          title: template.title,
          templateId: template.templateId,
        })),
      ]
    : [[]]

  const settingsForSecondScreen: Settings = {
    title: 'テンプレートをお選びください',
    backTitle: '業種選択に戻る',
    showIndustries: templateInfoList,
    onBack: () => {
      history.push('/templates/create/industry')
    },
    onClick: (industryInfo: IndustryInfo) => {
      if (!industryInfo.templateId) {
        history.push('/templates/pages/add')
      } else {
        const url = `/templates/${industryInfo.templateId}/pages/edit?industry=true`
        history.push(key ? `${url}&key=${key}` : url)
      }
    },
  }

  // `/templates/create/industry`と`/templates/create/type`の2つのパスで
  // このコンポーネントを使っているため、このような手当が必要。
  const settings: Settings = isSecondScreen
    ? settingsForSecondScreen
    : settingsForFirstScreen

  if (isSecondScreen && isLoading) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ height: '50vh' }}
      >
        <CircularProgress size={40} />
      </Stack>
    )
  }

  return (
    <>
      <TemplateHeader
        title={settings.title}
        subTitle={settings.subTitle}
        backTitle={settings.backTitle}
        onBack={() => settings.onBack()}
      />
      <Box mt={2}>
        <IndustryCardGrid
          showIndustries={settings.showIndustries}
          onClick={settings.onClick}
        />
      </Box>
    </>
  )
}

export default CreateTemplateIndustryScene
